import type { ComponentConfig } from "@puckeditor/core";
import { createPortal } from "react-dom";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useLanguageSwitcherRuntime } from "../../i18n/language-switcher-runtime";
import { cn } from "../../lib/cn";
import { backgroundColorField, textColorField } from "../../lib/fields";

const localeNames: Record<string, string> = {
  ar: "Arabic",
  de: "Deutsch",
  en: "English",
  es: "Espanol",
  fr: "Francais",
  it: "Italiano",
  nl: "Nederlands",
  pt: "Portugues",
};

const alignmentMap: Record<LanguageSwitcherProps["alignment"], string> = {
  left: "justify-start",
  center: "justify-center",
  right: "justify-end",
};

const sizeMap = {
  sm: {
    trigger: "min-h-8 px-2.5 py-1.5",
    item: "min-h-9 px-3 py-2",
    label: "text-[11px]",
    chevron: "h-3.5 w-3.5",
    check: "h-3.5 w-3.5",
  },
  md: {
    trigger: "min-h-9 px-3 py-2",
    item: "min-h-10 px-3.5 py-2.5",
    label: "text-xs",
    chevron: "h-4 w-4",
    check: "h-4 w-4",
  },
} as const;

type FloatingMenuPosition = {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
};

function pageSlugToPathname(pageSlug?: string | null) {
  const normalized = String(pageSlug || "")
    .trim()
    .replace(/^\/+|\/+$/g, "");

  if (!normalized || normalized === "home" || normalized === "index") {
    return "/";
  }

  return `/${normalized}`;
}

function buildLocalizedHref(
  pageSlug: string | null | undefined,
  locale: string,
  defaultLocale: string,
) {
  const basePathname = pageSlugToPathname(pageSlug);

  if (!locale || locale === defaultLocale) {
    return basePathname;
  }

  return basePathname === "/" ? `/${locale}` : `/${locale}${basePathname}`;
}

function getLocaleCode(locale: string) {
  return locale.toUpperCase();
}

function getLocaleName(locale: string) {
  return localeNames[locale] || getLocaleCode(locale);
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="m5 7.5 5 5 5-5" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="m5 10 3 3 7-7" />
    </svg>
  );
}

export interface LanguageSwitcherProps {
  display: "code" | "name";
  variant: "menu" | "segmented" | "pill" | "text";
  alignment: "left" | "center" | "right";
  size: "sm" | "md";
  backgroundColor: string;
  textColor: string;
  activeBackgroundColor: string;
  activeTextColor: string;
  borderColor: string;
  borderRadius: number;
}

export const defaultLanguageSwitcherProps: LanguageSwitcherProps = {
  display: "code",
  variant: "menu",
  alignment: "right",
  size: "sm",
  backgroundColor: "#ffffff",
  textColor: "#0f172a",
  activeBackgroundColor: "#0f172a",
  activeTextColor: "#ffffff",
  borderColor: "#cbd5e1",
  borderRadius: 5,
};

export const LanguageSwitcher = ({
  display = defaultLanguageSwitcherProps.display,
  variant = defaultLanguageSwitcherProps.variant,
  alignment = defaultLanguageSwitcherProps.alignment,
  size = defaultLanguageSwitcherProps.size,
  backgroundColor = defaultLanguageSwitcherProps.backgroundColor,
  textColor = defaultLanguageSwitcherProps.textColor,
  activeBackgroundColor = defaultLanguageSwitcherProps.activeBackgroundColor,
  activeTextColor = defaultLanguageSwitcherProps.activeTextColor,
  borderColor = defaultLanguageSwitcherProps.borderColor,
  borderRadius = defaultLanguageSwitcherProps.borderRadius,
  puck,
}: LanguageSwitcherProps & { puck?: { isEditing?: boolean } }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<FloatingMenuPosition | null>(
    null,
  );
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const runtime = useLanguageSwitcherRuntime();
  const currentLocale =
    runtime?.currentLocale || runtime?.defaultLocale || "en";
  const defaultLocale = runtime?.defaultLocale || "en";
  const fallbackLocales = runtime ? [currentLocale] : ["en", "de"];
  const locales = Array.from(
    new Set([...(runtime?.availableLocales || fallbackLocales), currentLocale]),
  ).filter(Boolean);

  const isEditing = puck?.isEditing;
  const sizeStyles = sizeMap[size];
  const effectiveDisplay = variant === "menu" ? "code" : display;
  const palette = {
    surface: backgroundColor,
    foreground: textColor,
    activeSurface: activeBackgroundColor,
    activeForeground: activeTextColor,
    border: borderColor,
  };
  const paletteStyle = {
    "--lang-bg": palette.surface,
    "--lang-fg": palette.foreground,
    "--lang-active-bg": palette.activeSurface,
    "--lang-active-fg": palette.activeForeground,
    "--lang-border": palette.border,
  } as CSSProperties;

  const syncMenuPosition = (panelHeight?: number) => {
    if (!triggerRef.current || typeof window === "undefined") {
      return;
    }

    const rect = triggerRef.current.getBoundingClientRect();
    const estimatedHeight = Math.min(
      panelHeight ?? locales.length * (size === "sm" ? 42 : 46) + 16,
      320,
    );
    const width = Math.max(Math.ceil(rect.width), 72);
    const gutter = 8;
    const spaceBelow = window.innerHeight - rect.bottom - gutter;
    const spaceAbove = rect.top - gutter;
    const openUpwards =
      spaceBelow < Math.min(estimatedHeight, 220) && spaceAbove > spaceBelow;

    let left = rect.right - width;
    if (alignment === "left") {
      left = rect.left;
    }
    if (alignment === "center") {
      left = rect.left + rect.width / 2 - width / 2;
    }

    left = Math.min(Math.max(gutter, left), window.innerWidth - width - gutter);

    const top = openUpwards
      ? Math.max(gutter, rect.top - estimatedHeight - gutter)
      : Math.min(
          window.innerHeight - estimatedHeight - gutter,
          rect.bottom + gutter,
        );

    setMenuPosition({
      top,
      left,
      width,
      maxHeight: Math.max(
        120,
        openUpwards ? Math.min(spaceAbove, 320) : Math.min(spaceBelow, 320),
      ),
    });
  };

  useEffect(() => {
    if (!menuOpen) {
      return;
    }

    syncMenuPosition(menuRef.current?.offsetHeight);

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };

    const handleViewportChange = () => {
      syncMenuPosition(menuRef.current?.offsetHeight);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [alignment, locales.length, menuOpen, size]);

  useEffect(() => {
    if (menuOpen && menuRef.current) {
      syncMenuPosition(menuRef.current.offsetHeight);
    }
  }, [menuOpen, locales.length, size]);

  useEffect(() => {
    setMenuOpen(false);
  }, [currentLocale]);

  if (locales.length < 2) {
    return <div hidden />;
  }

  if (variant === "menu") {
    const triggerLabel = getLocaleCode(currentLocale);

    return (
      <div
        className={cn("flex max-w-full", alignmentMap[alignment])}
        style={paletteStyle}
        data-language-switcher-root="true"
      >
        <div className="relative max-w-full">
          <button
            ref={triggerRef}
            type="button"
            onClick={() => setMenuOpen((current) => !current)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border shadow-none transition-[border-color,background-color,color] duration-150",
              sizeStyles.trigger,
            )}
            aria-label="Language selector"
            aria-expanded={menuOpen}
            aria-haspopup="listbox"
            style={
              {
                backgroundColor: palette.surface,
                borderColor: palette.border,
                borderRadius: `${borderRadius}px`,
                color: palette.foreground,
              } as CSSProperties
            }
          >
            <span
              className={cn(
                "font-semibold uppercase tracking-[0.18em] leading-none",
                sizeStyles.label,
              )}
            >
              {triggerLabel}
            </span>
            <ChevronIcon
              className={cn(
                sizeStyles.chevron,
                "shrink-0 transition-transform duration-200",
                menuOpen && "rotate-180",
              )}
            />
          </button>

          {menuOpen && menuPosition && typeof document !== "undefined"
            ? createPortal(
                <div
                  ref={menuRef}
                  className="fixed z-[10020] overflow-auto rounded-xl border p-1.5 shadow-none transition-[border-color,background-color,color] duration-150"
                  style={
                    {
                      top: menuPosition.top,
                      left: menuPosition.left,
                      width: menuPosition.width,
                      maxHeight: menuPosition.maxHeight,
                      backgroundColor: palette.surface,
                      borderColor: palette.border,
                      borderRadius: `${borderRadius}px`,
                      color: palette.foreground,
                    } as CSSProperties
                  }
                >
                  <div className="grid gap-1">
                    {locales.map((locale) => {
                      const active = locale === currentLocale;
                      const href = runtime?.buildHref
                        ? runtime.buildHref(locale)
                        : buildLocalizedHref(
                            runtime?.pageSlug,
                            locale,
                            defaultLocale,
                          );
                      const Tag = isEditing ? "button" : "a";
                      const localeProps = isEditing
                        ? {
                            type: "button" as const,
                            onClick: (event: React.MouseEvent) => {
                              event.preventDefault();
                              setMenuOpen(false);
                            },
                          }
                        : {
                            href,
                            onClick: () => setMenuOpen(false),
                          };

                      return (
                        <Tag
                          key={locale}
                          {...localeProps}
                          data-runtime-locale-link={
                            isEditing ? undefined : "true"
                          }
                          aria-current={active ? "page" : undefined}
                          aria-label={getLocaleName(locale)}
                          title={getLocaleName(locale)}
                          className={cn(
                            "flex w-full items-center justify-between rounded-xl border no-underline transition-[border-color,background-color,color] duration-150",
                            sizeStyles.item,
                          )}
                          style={
                            {
                              backgroundColor: active
                                ? palette.activeSurface
                                : "transparent",
                              color: active
                                ? palette.activeForeground
                                : palette.foreground,
                              borderColor: active
                                ? palette.activeSurface
                                : "transparent",
                              borderRadius: `${borderRadius}px`,
                            } as CSSProperties
                          }
                        >
                          <span
                            className={cn(
                              "font-semibold uppercase tracking-[0.18em] leading-none",
                              sizeStyles.label,
                            )}
                          >
                            {getLocaleCode(locale)}
                          </span>
                          {active ? (
                            <CheckIcon
                              className={cn(sizeStyles.check, "shrink-0")}
                            />
                          ) : null}
                        </Tag>
                      );
                    })}
                  </div>
                </div>,
                document.body,
              )
            : null}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn("flex max-w-full", alignmentMap[alignment])}
      style={paletteStyle}
      data-language-switcher-root="true"
    >
      <nav
        className={cn(
          "inline-flex flex-wrap items-center",
          variant === "segmented" &&
            "gap-1 rounded-full border p-1 shadow-[0_14px_36px_-30px_rgba(15,23,42,0.32)]",
          variant === "pill" && "gap-2 rounded-full border p-1",
          variant === "text" && "gap-3",
        )}
        aria-label="Language selector"
        style={
          {
            backgroundColor:
              variant === "text" ? "transparent" : palette.surface,
            borderColor: variant === "text" ? "transparent" : palette.border,
          } as CSSProperties
        }
      >
        {locales.map((locale) => {
          const active = locale === currentLocale;
          const href = runtime?.buildHref
            ? runtime.buildHref(locale)
            : buildLocalizedHref(runtime?.pageSlug, locale, defaultLocale);
          const Tag = isEditing ? "button" : "a";
          const localeProps = isEditing
            ? {
                type: "button" as const,
                onClick: (event: React.MouseEvent) => event.preventDefault(),
              }
            : { href };

          return (
            <Tag
              key={locale}
              {...localeProps}
              data-runtime-locale-link={isEditing ? undefined : "true"}
              aria-current={active ? "page" : undefined}
              aria-label={getLocaleName(locale)}
              title={getLocaleName(locale)}
              className={cn(
                "inline-flex items-center justify-center no-underline transition-all duration-150",
                variant === "text"
                  ? "border-b-2 px-0 pb-1"
                  : cn("rounded-full border", sizeStyles.item),
              )}
              style={
                {
                  color:
                    variant === "text"
                      ? active
                        ? palette.activeSurface
                        : palette.foreground
                      : active
                        ? palette.activeForeground
                        : palette.foreground,
                  backgroundColor:
                    variant === "text"
                      ? "transparent"
                      : active
                        ? palette.activeSurface
                        : variant === "pill"
                          ? "rgba(255,255,255,0.72)"
                          : "transparent",
                  borderColor:
                    variant === "text"
                      ? active
                        ? palette.activeSurface
                        : "transparent"
                      : active
                        ? palette.activeSurface
                        : palette.border,
                  borderRadius: `${borderRadius}px`,
                } as CSSProperties
              }
            >
              <span
                className={cn(
                  "font-semibold uppercase tracking-[0.18em] leading-none",
                  sizeStyles.label,
                )}
              >
                {effectiveDisplay === "name"
                  ? getLocaleName(locale)
                  : getLocaleCode(locale)}
              </span>
            </Tag>
          );
        })}
      </nav>
    </div>
  );
};

export const languageSwitcherConfig: ComponentConfig<LanguageSwitcherProps> = {
  label: "Language Switcher",
  fields: {
    display: {
      type: "select",
      label: "Display",
      options: [
        { label: "Locale code", value: "code" },
        { label: "Language name", value: "name" },
      ],
    },
    variant: {
      type: "select",
      label: "Style",
      options: [
        { label: "Menu / Popover", value: "menu" },
        { label: "Segmented", value: "segmented" },
        { label: "Pills", value: "pill" },
        { label: "Text", value: "text" },
      ],
    },
    alignment: {
      type: "select",
      label: "Alignment",
      options: [
        { label: "Left", value: "left" },
        { label: "Center", value: "center" },
        { label: "Right", value: "right" },
      ],
    },
    size: {
      type: "select",
      label: "Size",
      options: [
        { label: "Small", value: "sm" },
        { label: "Medium", value: "md" },
      ],
    },
    backgroundColor: { ...backgroundColorField, label: "Surface Color" },
    textColor: { ...textColorField, label: "Text Color" },
    activeBackgroundColor: {
      ...backgroundColorField,
      label: "Active Surface Color",
    },
    activeTextColor: { ...textColorField, label: "Active Text Color" },
    borderColor: { ...textColorField, label: "Border Color" },
    borderRadius: {
      type: "number",
      label: "Corner Radius (px)",
      min: 0,
      max: 999,
    },
  },
  defaultProps: {
    ...defaultLanguageSwitcherProps,
  },
  render: LanguageSwitcher,
};
